// npm packages
import React, { useState, useEffect, useContext, useRef } from "react";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";

// custom pages
import "./qualityDashboard.css";
import config from "config/config";
import Context from "context/context";
import leftarrow from "assets/images/arrow-right.png";
import ReusableTable from "components/table/table";
import QualityDetails from "./qualityDetails";

// material ui
import { Autocomplete, Box, Switch, CircularProgress } from "@mui/material";
import debounce from "lodash.debounce";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Tooltip from "@mui/material/Tooltip";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Select, MenuItem } from '@mui/material';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    IconButton,
    FormControlLabel,
    Divider,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Breadcrumbs, Link } from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Grid } from "../../../../../node_modules/@mui/material/index";
import "./QualitySettings.css";
import {
    Radio,
    RadioGroup,
    FormControl,
    FormLabel,
} from "@mui/material";

const QualitySettings = () => {

    const navigate = useNavigate();
    const { state, dispatch } = useContext(Context);

    const [sectionOptions, setSectionOptions] = useState([]);
    const [selectedValue, setSelectedValue] = useState("");
    const [isAliasModalOpen, setIsAliasModalOpen] = useState(false);
    const [aliasSelectedValue, setAliasSelectedValue] = useState(sectionOptions[0]);
    const [aliasInput, setAliasInput] = useState("");
    const [newSectionName, setNewSectionName] = useState("");
    const [aliasList, setAliasList] = useState([]);
    const [aliasCount, setAliasCount] = useState(0);
    const [offset, setOffset] = useState(1);
    const [limit, setLimit] = useState(10);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedAlias, setSelectedAlias] = useState(null);
    const [editedAliasName, setEditedAliasName] = useState("");
    const [addSection, setAddSection] = useState("no");

    const handleChange = (event) => {
        setSelectedValue(event.target.value);
    };

    const handleAddAlias = () => {
        setIsAliasModalOpen(true);
    };

    useEffect(() => {
        if (selectedValue) {
            fetchAliasesBySection();
        }
    }, [selectedValue, offset, limit]);


    useEffect(() => {
        if (sectionOptions.length > 0 && !selectedValue) {
            setSelectedValue(sectionOptions[0]);
        }
    }, [sectionOptions]);


    const fetchAliasesBySection = async () => {
        if (!selectedValue) return;
        dispatch({ type: "SET_LOADING", payload: true });

        try {
            const query = encodeURIComponent(
                JSON.stringify({
                    offset: (offset - 1) * limit,
                    limit,
                    sectionName: selectedValue,
                })
            );

            const response = await fetch(
                `${config.host}/panel/quality-analyzer/alias-names?query=${query}`,
                {
                    method: "GET",
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );

            const result = await response.json();

            if (result.status === "Ok" && Array.isArray(result.data.data)) {
                setAliasList(result.data.data);
                setAliasCount(result.data.count);
                dispatch({ type: "SET_LOADING", payload: false });
            } else {
                console.warn("Unexpected alias response:", result);
                dispatch({ type: "SET_LOADING", payload: false });
            }
        } catch (error) {
            console.error("Error fetching alias names:", error);
            dispatch({ type: "SET_LOADING", payload: false });
        }
    };


    const aliasTableData = aliasList.map((alias, index) => [
        index + 1 + (offset - 1) * limit,
        alias.aliasName,

        <IconButton
            size="small"
            onClick={() => handleEditAlias(alias)}
            color="#FE602F"
        >
            <EditIcon />
        </IconButton>,
        <IconButton
            size="small"
            onClick={() => handleDeleteAlias(alias.aliasId)}
            color="error"
        >
            <DeleteIcon />
        </IconButton>

    ]);

    const handleEditAlias = (alias) => {
        setSelectedAlias(alias);
        setEditedAliasName(alias.aliasName);
        setIsEditModalOpen(true);
    };

    const handleUpdateAlias = async () => {
        if (!editedAliasName.trim() || !selectedAlias?.aliasId) return;

        dispatch({ type: "SET_LOADING", payload: true });

        try {
            const res = await fetch(`${config.host}/panel/quality-analyzer/update-alias-name`, {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    aliasName: editedAliasName.trim(),
                    aliasId: selectedAlias.aliasId
                }),
            });

            const data = await res.json();

            if (data?.data?.updated === 1) {
                toast.success("Alias updated successfully!");
                setIsEditModalOpen(false);
                fetchAliasesBySection();
                dispatch({ type: "SET_LOADING", payload: false });
            } else {
                toast.error("Alias update failed.");
                dispatch({ type: "SET_LOADING", payload: false });
            }
        } catch (err) {
            toast.error("Something went wrong.");
            dispatch({ type: "SET_LOADING", payload: false });
        }
    };


    const handleDeleteAlias = async (aliasId) => {
        dispatch({ type: "SET_LOADING", payload: true });
        try {
            const res = await fetch(`${config.host}/panel/quality-analyzer/remove-alias-name`, {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ aliasId }),
            });

            const data = await res.json();

            if (data?.data?.updated === 1) {
                toast.success("Alias deleted successfully");
                fetchAliasesBySection();
                dispatch({ type: "SET_LOADING", payload: false });
            } else {
                toast.error(data?.message || "Alias deletion failed");
                dispatch({ type: "SET_LOADING", payload: false });
            }
        } catch (error) {
            toast.error("Something went wrong while deleting");
            dispatch({ type: "SET_LOADING", payload: false });
        }
    };


    useEffect(() => {
        fetchSectionNames();
    }, []);
    const fetchSectionNames = async () => {
        dispatch({ type: "SET_LOADING", payload: true });
        try {
            const response = await fetch(`${config.host}/panel/quality-analyzer/alias/section-names`, {
                method: "GET",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
                dispatch({ type: "SET_LOADING", payload: false });
            }

            const result = await response.json();

            if (result.status === "Ok" && result.data?.sectionNames) {
                setSectionOptions(result.data.sectionNames);
                dispatch({ type: "SET_LOADING", payload: false });
            } else {
                console.warn("Unexpected response:", result);
                dispatch({ type: "SET_LOADING", payload: false });
            }
        } catch (error) {
            console.error("Error fetching section names:", error);
            dispatch({ type: "SET_LOADING", payload: false });
        }
    };


    const handleCloseAliasModal = () => {
        setIsAliasModalOpen(false);
        setAliasInput("");
        setNewSectionName("");
    };

    const handleAliasRadioChange = (e) => {
        setAliasSelectedValue(e.target.value);
    };



    const handleAddSection = async () => {
        if (!newSectionName.trim()) {
            toast.warn("Section name cannot be empty");
            return;
        }
        dispatch({ type: "SET_LOADING", payload: true });

        try {
            const response = await fetch(`${config.host}/panel/quality-analyzer/alias/section-name`, {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ sectionName: newSectionName.trim() }),
            });

            const result = await response.json();

            if (result.status === "Ok") {
                toast.success("Section added successfully");
                fetchSectionNames();
                setNewSectionName("");
                setIsAliasModalOpen(false);
                dispatch({ type: "SET_LOADING", payload: false });
            } else {
                toast.error(result.message || "Failed to add section");
                dispatch({ type: "SET_LOADING", payload: false });
            }
        } catch (error) {
            console.error("Error adding section:", error);
            toast.error("Something went wrong. Please try again.");
            dispatch({ type: "SET_LOADING", payload: false });
        }
    };

    const handleAddAliasSubmit = async () => {
        if (!aliasInput || !selectedValue) {
            toast.error("Please enter an alias and select a section");
            return;
        }
        dispatch({ type: "SET_LOADING", payload: true });
        try {
            const response = await fetch(`${config.host}/panel/quality-analyzer/alias-name`, {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    aliasName: aliasInput,
                    sectionName: selectedValue,
                }),
            });

            const result = await response.json();

            if (result.status === "Ok") {
                toast.success("Alias added successfully!");
                setAliasInput(""); // Clear input
                fetchAliasesBySection()
                setIsAliasModalOpen(false); // Close modal
                dispatch({ type: "SET_LOADING", payload: false });
            } else {
                toast.error(result.message || "Failed to add alias");
                dispatch({ type: "SET_LOADING", payload: false });
            }
        } catch (error) {
            console.error("Error adding alias:", error);
            toast.error("Something went wrong. Please try again.");
            dispatch({ type: "SET_LOADING", payload: false });
        }
    };



    return (
        <div className="quality-dashboard">

            {/* Main Content */}
            <div className="dashboard-content">
                <div className="header-container">
                    <h1 className="dashboard-title">Quality Settings</h1>

                    <div onClick={() => navigate("/panel/rpHomePage")} className="back-button">
                        <img src={leftarrow} alt="Back" />
                        <span>Back</span>
                    </div>

                    <div className="button-container">
                        <Button
                            variant="contained"
                            className="add-alias-btn"
                            onClick={handleAddAlias}
                        >
                            Add Alias
                        </Button>


                    </div>

                    <div className="radio-section">
                        <FormControl component="fieldset">
                            <FormLabel component="legend" className="radio-label">Select a Section</FormLabel>
                            <RadioGroup
                                row
                                value={selectedValue}
                                onChange={handleChange}
                                className="radio-group"
                            >
                                {sectionOptions.map((option, index) => (
                                    <FormControlLabel
                                        key={index}
                                        value={option}
                                        control={
                                            <Radio
                                                sx={{
                                                    color: '#888',
                                                    '&.Mui-checked': {
                                                        color: '#E97000',
                                                    },
                                                }}
                                            />
                                        }
                                        label={option}
                                        className="radio-option"
                                    />
                                ))}
                            </RadioGroup>
                        </FormControl>
                    </div>

                    <ReusableTable
                        data={aliasTableData}
                        count={aliasCount}
                        offset={offset}
                        setOffset={setOffset}
                        limit={limit}
                        setSearchValue={() => { }} // Optional, implement if you want search
                        heading={["S.No", "Alias Name", "Edit", "Delete"]}
                    />

                </div>





            </div>
            <Dialog open={isAliasModalOpen} onClose={handleCloseAliasModal} fullWidth maxWidth="sm">
                <DialogTitle className="dialog-title-modern">
                    Add Alias
                    <IconButton onClick={handleCloseAliasModal} className="dialog-close-icon">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent className="dialog-box-container-modern">
                    <FormControl fullWidth size="small">
                        <FormLabel className="radio-label-modern">Select a Section</FormLabel>
                        <Select
                            value={selectedValue}
                            onChange={handleChange}
                            className="text-field-modern"
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight: 200, // Scroll if options exceed this height
                                    },
                                },
                            }}
                        >
                            {sectionOptions.map((option) => (
                                <MenuItem key={option} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>


                    <Typography className="input-label-modern">
                        Enter Alias for {selectedValue}
                    </Typography>
                    <TextField
                        variant="outlined"
                        size="small"
                        className="text-field-modern"
                        value={aliasInput}
                        onChange={(e) => setAliasInput(e.target.value)}
                    />

                    <Button variant="contained" className="add-btn-modern" onClick={handleAddAliasSubmit}>
                        Add Alias
                    </Button>

                    <Divider className="section-divider-modern" />
                    <Typography className="section-note-modern">
                        Do you want to add a new section?
                    </Typography>
                    <FormControl component="fieldset" className="radio-group-modern">
                        <RadioGroup row value={addSection} onChange={(e) => setAddSection(e.target.value)}>
                            <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                            <FormControlLabel value="no" control={<Radio />} label="No" />
                        </RadioGroup>
                    </FormControl>

                    {/* Conditionally Display Section Name Input */}
                    {addSection === 'yes' && (
                        <>
                            <Typography className="input-label-modern">Section Name</Typography>
                            <TextField
                                variant="outlined"
                                size="small"
                                className="text-field-modern"
                                value={newSectionName}
                                onChange={(e) => setNewSectionName(e.target.value)}
                            />
                            <Button
                                variant="contained"
                                className="add-btn-modern"
                                onClick={handleAddSection}
                            >
                                Add Section
                            </Button>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            <Dialog open={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle className="dialog-title-modern">
                    Edit Alias
                    <IconButton onClick={() => setIsEditModalOpen(false)} className="dialog-close-icon">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent className="dialog-box-container-modern">
                    <Typography className="input-label-modern">Alias Name</Typography>
                    <TextField
                        fullWidth
                        size="small"
                        variant="outlined"
                        className="text-field-modern"
                        value={editedAliasName}
                        onChange={(e) => setEditedAliasName(e.target.value)}
                    />

                    <Button
                        variant="contained"
                        className="add-btn-modern"
                        onClick={handleUpdateAlias}
                    >
                        Update
                    </Button>
                </DialogContent>
            </Dialog>








        </div>

    );
};

export default QualitySettings;
